import {
  IonCard,
  IonContent,
  IonLabel,
  IonLoading,
  IonPage,
  useIonAlert,
} from "@ionic/react";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import PageHeader from "../components/PageHeader";
import {
  TranslationMessagesContext,
  TranslationsContext,
} from "../util/Translations";
import { useParams } from "react-router";
import { decodeParam, encodeParam } from "../util/ApiHelper";
import { GetWorkOrdersForEquipment } from "../api/EquipmentHierarchys";
import {
  ColorCodes,
  FieldDef,
  updatePriorityCodes,
  updateStatusCodes,
  maxResults,
  QueryResult,
  updateNoOfFieldsToDisplay,
} from "../hooks/useQueryExec";
import CardFilter from "../components/CardFilter";
import {
  getUserRequesterAccess,
  getUserTechnicianAccess,
} from "../util/UserHelper";
import CardInfo from "../components/CardInfo";
import Fuse from "fuse.js";
import { reactPlugin } from "../util/AppInsights";
import { withAITracking } from "@microsoft/applicationinsights-react-js";

type Params = {
  equipId: string;
  isOpened: string;
  includeChildren: string;
  partType: string;
  isEqPmSchedule: string;
};

const EquipmentWorkOrder: React.FC = () => {
  const { translations } = useContext(TranslationsContext);
  const { translatedMessages } = useContext(TranslationMessagesContext);

  const { equipId, includeChildren, isOpened, partType, isEqPmSchedule } =
    useParams<Params>();
  const displayEquipId = decodeParam(equipId);
  const [showLoading, setShowLoading] = useState(false);
  const [errorState, setErrorState] = useState("");
  const [totalCount, setTotalCount] = useState<number>(0);

  let [noOfFieldsToDisplay, setNoOfFieldsToDisplay] = useState<number>();
  const [queryRows, setQueryRows] = useState<QueryResult[]>([]);
  const [filterRows, setFilterRows] = useState<QueryResult[]>([]);

  const [searchExecuted, setSearchExecuted] = useState(false);
  const [presentAlert] = useIonAlert();
  const [woLink, setWoLink] = useState("");
  const [resultsFilter, setResultsFilter] = useState("");

  const statusCodes = useRef<ColorCodes[]>([]);
  const priorityCodes = useRef<ColorCodes[]>([]);

  const isOpenWorkOrder = isOpened?.toLowerCase?.() === "true";
  const shouldIncludeChildren = includeChildren?.toLowerCase?.() === "true";
  const isEquipmentPmSchedule = isEqPmSchedule?.toLowerCase?.() === "true";

  let subtitle = "";

  if (isEquipmentPmSchedule) {
    subtitle =
      translations["lbl_mob_pm_schedules_for_equipment"] ||
      "PM Schedules for Equipment";
  } else if (isOpenWorkOrder) {
    subtitle = shouldIncludeChildren
      ? translations["lbl_mob_open_work_orders_for_equipment_and_children"] ||
        "Open Work Orders for Equipment And Children"
      : translations["lbl_mob_pm_schedules_for_equipment"] ||
        "Open Work Orders for Equipment";
  } else {
    subtitle = shouldIncludeChildren
      ? translations[
          "lbl_mob_complete_work_orders_for_equipment_and_children"
        ] || "Completed Work Orders for Equipment And Children"
      : translations["lbl_mob_complete_work_orders_for_equipment"] ||
        "Completed Work Orders for Equipment";
  }

  let queryName: string;
  let moduleName: string;
  let columnAlias: string;
  let tableAlias: string;
  let keyFieldName: string = "";

  if (isEquipmentPmSchedule) {
    queryName = "SYS PM Schedule For Equipment";
    moduleName = "PM Schedule";
    columnAlias = "equip_serial_id";
    tableAlias = "wo_v_pm_schedule";
  } else {
    queryName = isOpenWorkOrder
      ? "SYS Open WOs Hierarchy"
      : "SYS Closed WOs Hierarchy";
    moduleName = "Work Order";
    columnAlias = "equipment";
    tableAlias = "wo_work_order";
    keyFieldName = "cf_work_order";
  }

  const processError = useCallback(
    (errorData: any) => {
      if (errorData) {
        if (errorData.includes("relogin")) {
          setErrorState(
            translatedMessages["SessionExpired"]?.MessageText ||
              "Your session has expired. Please re-login."
          );
        } else {
          setErrorState(errorData);
        }
      }
    },
    [translatedMessages]
  );

  const getWorkOrder = useCallback(async () => {
    let fields: FieldDef[] = [];
    let finalQueryResult = [];

    const filters = isEquipmentPmSchedule
      ? [
          {
            TableAlias: "wo_v_pm_schedule",
            ColumnAlias: "equip_serial_id",
            Operator: "=",
            ParameterValue: displayEquipId,
          },
          {
            TableAlias: "wo_v_pm_schedule",
            ColumnAlias: "equip_serial_ind",
            Operator: "=",
            ParameterValue: "e",
          },
        ]
      : [];

    const response = await GetWorkOrdersForEquipment({
      QueryName: queryName,
      ModuleName: moduleName,
      EnableMultiPlantExecution: false,
      IsPublicQuery: true,
      FilterParameters: filters,
      AskAtExecutionCriterias: [
        {
          ColumnAlias: columnAlias,
          ComparisonExpression: displayEquipId,
          CriteriaSequence: 3,
          JoinID: 0,
          TableAlias: tableAlias,
          CompanionElement: null,
          CompanionExpression: null,
        },
      ],
      includeChildren: shouldIncludeChildren,
      parentId: displayEquipId,
      parentType: Number(partType),
    });
    if (response.status !== 200) {
      setErrorState(response.data);
    } else {
      let counter = 0;
      let queryData: QueryResult[] = [];
      if (response.data && !response.data.Success) {
        processError(response.data.Messages[0].Text);
      }
      if (response.data?.Success) {
        setTotalCount(Number.parseInt(response.data.Count));
        if (response.data?.QueryData?.QwColumn) {
          response.data.QueryData.QwColumn.forEach((field: FieldDef) => {
            fields.push(field);
          });
        }
        finalQueryResult = response.data.QueryResult;

        finalQueryResult.forEach((res: any) => {
          queryData.push({
            HeaderRow: counter,
            HeaderName: keyFieldName,
            HeaderText: keyFieldName,
            HeaderType: "String",
            HeaderValue: "",
            HeaderColor: "",
            Columns: [],
          });
          let cols = Object.entries(res);
          cols.forEach((col, index) => {
            //get the label from field def
            let field = fields.find((f) => {
              return col[0] === `${f.ColumnName}_${f.JoinId}`;
            });
            queryData[counter].Columns.push({
              Order: field !== undefined ? field.DisplayOrder : index,
              Name: col[0],
              Text: field !== undefined ? field.TranslatedIdText : col[0],
              Value: col[1] as string,
              Type: field !== undefined ? field.DataType : "String",
              Hidden: false,
            });
            if (field !== undefined && field.ColumnName === keyFieldName) {
              queryData[counter].HeaderText = field.TranslatedIdText;
              queryData[counter].HeaderValue = col[1] as string;
              queryData[counter].Columns[index].Hidden = true;
            }
          });
          counter++;
        });
        let pf = fields.find((f) => {
          return f.ColumnName === "priority";
        });
        let sf = fields.find((f) => {
          return f.ColumnName === "status";
        });
        let pc: ColorCodes[] = priorityCodes.current;
        let sc: ColorCodes[] = statusCodes.current;
        if (pc.length === 0 && sc.length === 0) {
          pc = await updatePriorityCodes(
            processError,
            (codes) => (priorityCodes.current = codes)
          );
          sc = await updateStatusCodes(
            processError,
            (codes) => (statusCodes.current = codes)
          );
        }
        if (pc.length > 0 && pf) {
          queryData.forEach((r) => {
            let pCode = r.Columns.find((c) => {
              return c.Name === `${pf?.ColumnName}_${pf?.JoinId}`;
            });
            r.HeaderColor =
              pc.find((c) => {
                return c.Code === pCode?.Value;
              })?.Color ?? "";
          });
        } else if (sc.length > 0 && sf) {
          queryData.forEach((r) => {
            let pCode = r.Columns.find((c) => {
              return c.Name === `${sf?.ColumnName}_${sf?.JoinId}`;
            });
            r.HeaderColor =
              statusCodes.current.find((c) => {
                return c.Code === pCode?.Value;
              })?.Color ?? "";
          });
        }
      }

      if (noOfFieldsToDisplay) {
        for (const item of queryData) {
          item.Columns.sort((a, b) => {
            return a.Order - b.Order;
          }); //sort explicitly for iOS bug
          item.Columns = item.Columns.slice(0, noOfFieldsToDisplay);
        }
      }
      setQueryRows(queryData);
      setFilterRows(queryData.slice(0, maxResults));
    }
  }, [
    columnAlias,
    displayEquipId,
    isEquipmentPmSchedule,
    keyFieldName,
    moduleName,
    noOfFieldsToDisplay,
    partType,
    processError,
    queryName,
    shouldIncludeChildren,
    tableAlias,
  ]);

  async function setWorkOrderPath() {
    if (await getUserTechnicianAccess()) {
      setWoLink("/workorder");
    } else if (await getUserRequesterAccess()) {
      setWoLink("/requester/workorder");
    }
  }

  useEffect(() => {
    (async () => {
      setShowLoading(true);
      await setWorkOrderPath();

      await updatePriorityCodes(processError, (codes) => {
        priorityCodes.current = codes;
      });
      if (noOfFieldsToDisplay === undefined)
        await updateNoOfFieldsToDisplay(
          "NoOfFieldsToDisplay",
          setNoOfFieldsToDisplay
        );
      await getWorkOrder();
      setSearchExecuted(true);
      setShowLoading(false);
    })();
  }, [getWorkOrder, noOfFieldsToDisplay, processError]);

  const fuse = useMemo(
    () =>
      new Fuse(queryRows, {
        keys: ["Columns.Value"],
        includeScore: true,
        minMatchCharLength: 2,
        threshold: 0.5,
        shouldSort: true,
      }),
    [queryRows]
  );

  const execFilter = useCallback(
    async (queryString: string) => {
      if (queryString) {
        const res = fuse.search(queryString);
        setFilterRows(res.map((r) => r.item).slice(0, maxResults));
      } else {
        setFilterRows(queryRows.slice(0, maxResults));
      }
      setResultsFilter(queryString);
    },
    [fuse, queryRows]
  );

  return (
    <IonPage>
      <IonLoading isOpen={showLoading} />

      <PageHeader
        title={`${
          translations["lbl_category_equipment"] || "Equipment"
        } ${displayEquipId}`}
        subtitle={subtitle}
        // subtitle={translations["lbl_open_work_orders"] || "Open Work Orders"}
        showBack
      />
      <IonContent className="ion-padding">
        {errorState ? (
          <div style={{ padding: 8 }} hidden={errorState === ""}>
            <IonLabel color={"danger"}>{errorState}</IonLabel>
          </div>
        ) : (
          <div
            style={{ padding: 8 }}
            hidden={showLoading || !searchExecuted || totalCount > 0}
          >
            <IonLabel color={"warning"}>
              {translatedMessages["NoResultsFound"]?.MessageText ||
                "No results found."}
            </IonLabel>
          </div>
        )}

        {!!queryRows && queryRows.length > 0 && (
          <>
            <CardFilter
              queryRows={queryRows}
              resultsFilter={resultsFilter}
              filterMethod={execFilter}
            />
            {filterRows.map(function (row) {
              return (
                <IonCard
                  style={
                    row.HeaderColor !== ""
                      ? {
                          borderColor: row.HeaderColor,
                          borderLeftStyle: "solid",
                          borderLeftWidth: "5px",
                        }
                      : {}
                  }
                  key={row.HeaderRow}
                  // navigate to work order details screen from work order/main page and stores
                  //stores may change to go to issue or returns directly
                  routerLink={
                    row.HeaderValue !== "" && !isEquipmentPmSchedule
                      ? `${woLink}/${encodeParam(row.HeaderValue)}`
                      : undefined
                  }
                  onClick={
                    !isEquipmentPmSchedule
                      ? () => {
                          if (row.HeaderValue === "") {
                            presentAlert(
                              translatedMessages[
                                "QWColumnMissing"
                              ]?.MessageText.replace("@1", row.HeaderValue) ||
                                `Cannot continue with this request. The column ${row.HeaderValue} is not selected in the query. Please rebuild the query and select this column.`
                            );
                          }
                        }
                      : undefined
                  }
                >
                  <CardInfo header={row.HeaderValue} columns={row.Columns} />
                </IonCard>
              );
            })}
          </>
        )}
      </IonContent>
    </IonPage>
  );
};

export default withAITracking(
  reactPlugin,
  EquipmentWorkOrder,
  "EquipmentWorkOrder"
);
